import { useState, useEffect, useCallback, useContext, useRef } from 'react';
import axios from 'axios';
import { AuthContext } from '../../contexts/AuthContext';
import './Locations.css';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000';

const TYPE_LABEL = {
  WAREHOUSE:       'Warehouse',
  DELIVERY_BOY:    'Delivery Boy',
  STORE:           'Store',
  VENDING_MACHINE: 'Vending Machine',
  KITCHEN:         'Kitchen',
};

const TYPE_CLASS = {
  WAREHOUSE:       'loc-badge--warehouse',
  DELIVERY_BOY:    'loc-badge--delivery_boy',
  STORE:           'loc-badge--store',
  VENDING_MACHINE: 'loc-badge--vending_machine',
  KITCHEN:         'loc-badge--kitchen',
};

const EMPTY_FORM = {
  location_type:  'WAREHOUSE',
  name:           '',
  reference_id:   '',
  address:        '',
  is_active:      true,
};

const EMPTY_ROW = { product_id: '', quantity: '', notes: '' };

/* ── Add / Edit Location Modal ─────────────────────────────── */
const LocationModal = ({ initial, auth, onClose, onSaved }) => {
  const isEdit = !!initial;
  const [form, setForm] = useState(isEdit ? {
    location_type:  initial.location_type,
    name:           initial.name,
    reference_id:   initial.reference_id ?? '',
    address:        initial.address ?? '',
    is_active:      initial.is_active,
  } : { ...EMPTY_FORM });
  const [users, setUsers]   = useState([]);
  const [saving, setSaving] = useState(false);
  const [error, setError]   = useState('');

  useEffect(() => {
    if (['DELIVERY_BOY', 'STORE', 'VENDING_MACHINE'].includes(form.location_type)) {
      setUsers([]);
      axios.get(`${API_URL}/stock/ref-users`, { params: { location_type: form.location_type }, ...auth })
        .then(r => setUsers(r.data || []))
        .catch(() => setUsers([]));
    } else {
      setUsers([]);
    }
  }, [form.location_type]); // eslint-disable-line

  const set = (field, value) => setForm(f => ({ ...f, [field]: value }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) { setError('Name is required.'); return; }
    setSaving(true);
    setError('');
    try {
      const payload = {
        location_type: form.location_type,
        name:          form.name.trim(),
        reference_id:  ['DELIVERY_BOY', 'STORE', 'VENDING_MACHINE'].includes(form.location_type) && form.reference_id ? Number(form.reference_id) : null,
        address:       form.address.trim() || null,
        is_active:     form.is_active,
      };
      if (isEdit) {
        await axios.put(`${API_URL}/stock/locations/${initial.id}`, payload, auth);
      } else {
        await axios.post(`${API_URL}/stock/locations`, payload, auth);
      }
      onSaved();
    } catch (err) {
      setError(err.response?.data?.error || 'Save failed.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="loc-overlay" onClick={onClose}>
      <div className="loc-modal" onClick={e => e.stopPropagation()}>
        <div className="loc-modal-header">
          <h3 className="loc-modal-title">{isEdit ? 'Edit Location' : 'Add Location'}</h3>
          <button className="loc-modal-close" onClick={onClose}>✕</button>
        </div>
        <form onSubmit={handleSubmit}>
          <div className="loc-modal-body">
            {error && <div className="loc-modal-error">{error}</div>}

            <div className="loc-field">
              <label className="loc-label">Type</label>
              <select className="loc-form-select" value={form.location_type} onChange={e => set('location_type', e.target.value)}>
                {Object.entries(TYPE_LABEL).map(([v, l]) => (
                  <option key={v} value={v}>{l}</option>
                ))}
              </select>
            </div>

            <div className="loc-field">
              <label className="loc-label">Name *</label>
              <input className="loc-input" type="text" placeholder="Location name" value={form.name} onChange={e => set('name', e.target.value)} required />
            </div>

            {['DELIVERY_BOY', 'STORE', 'VENDING_MACHINE'].includes(form.location_type) && (
              <div className="loc-field">
                <label className="loc-label">
                  {form.location_type === 'DELIVERY_BOY' ? 'Delivery Boy (Supply Partner)'
                    : form.location_type === 'STORE' ? 'Store Owner'
                    : 'Vending Machine'}
                </label>
                <select className="loc-form-select" value={form.reference_id} onChange={e => set('reference_id', e.target.value)}>
                  <option value="">— none —</option>
                  {users.map(u => (
                    <option key={u.id} value={u.id}>
                      {u.name || u.username}{u.phone ? ` · ${u.phone}` : ''}
                    </option>
                  ))}
                </select>
              </div>
            )}

            <div className="loc-field">
              <label className="loc-label">Address / Notes</label>
              <textarea className="loc-textarea" placeholder="Optional address or description" value={form.address} onChange={e => set('address', e.target.value)} />
            </div>

            <label className="loc-checkbox-row">
              <input type="checkbox" checked={form.is_active} onChange={e => set('is_active', e.target.checked)} />
              Active
            </label>
          </div>
          <div className="loc-modal-footer">
            <button type="button" className="loc-btn loc-btn--cancel" onClick={onClose}>Cancel</button>
            <button type="submit" className="loc-btn loc-btn--save" disabled={saving}>{saving ? 'Saving…' : 'Save'}</button>
          </div>
        </form>
      </div>
    </div>
  );
};

/* ── Product Picker (autocomplete) ────────────────────────── */
const ProductPicker = ({ products, value, onChange }) => {
  const selected   = products.find(p => p.id === value) || null;
  const [query,    setQuery]    = useState('');
  const [open,     setOpen]     = useState(false);
  const wrapRef    = useRef(null);

  const filtered = query.trim()
    ? products.filter(p => p.name.toLowerCase().includes(query.toLowerCase()))
    : products;

  useEffect(() => {
    const handler = (e) => {
      if (wrapRef.current && !wrapRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, []);

  const handleSelect = (p) => {
    onChange(p.id);
    setQuery('');
    setOpen(false);
  };

  const handleClear = (e) => {
    e.stopPropagation();
    onChange('');
    setQuery('');
    setOpen(false);
  };

  return (
    <div className="pp-wrap" ref={wrapRef}>
      <div className="pp-input-row" onClick={() => setOpen(o => !o)}>
        {selected && !open ? (
          <>
            <span className="pp-selected-name">{selected.name}</span>
            <button type="button" className="pp-clear" onClick={handleClear}>✕</button>
          </>
        ) : (
          <input
            className="pp-input"
            type="text"
            placeholder={selected ? selected.name : 'Search product…'}
            value={query}
            autoFocus={open}
            onChange={e => { setQuery(e.target.value); setOpen(true); }}
            onClick={e => { e.stopPropagation(); setOpen(true); }}
          />
        )}
        <span className="pp-arrow">{open ? '▲' : '▼'}</span>
      </div>
      {open && (
        <ul className="pp-dropdown">
          {filtered.length === 0 ? (
            <li className="pp-empty">No products found</li>
          ) : filtered.slice(0, 50).map(p => (
            <li
              key={p.id}
              className={`pp-option${p.id === value ? ' pp-option--active' : ''}`}
              onMouseDown={() => handleSelect(p)}
            >
              {p.name}
            </li>
          ))}
        </ul>
      )}
    </div>
  );
};

/* ── Add Stock Modal ───────────────────────────────────────── */
const AddStockModal = ({ location, auth, onClose, onDone }) => {
  const [products, setProducts] = useState([]);
  const [rows, setRows]         = useState(() => Array(5).fill(null).map(() => ({ ...EMPTY_ROW })));
  const [saving, setSaving]     = useState(false);
  const [error, setError]       = useState('');

  useEffect(() => {
    axios.get(`${API_URL}/products/`, auth)
      .then(r => setProducts((r.data || []).filter(p => p.status === 'A').sort((a, b) => a.name.localeCompare(b.name))))
      .catch(() => setProducts([]));
  }, []); // eslint-disable-line

  const setRow = (i, field, value) =>
    setRows(prev => prev.map((r, idx) => idx === i ? { ...r, [field]: value } : r));

  const handleSubmit = async () => {
    const lines = rows
      .filter(r => r.product_id && parseFloat(r.quantity) > 0)
      .map(r => ({ product_id: Number(r.product_id), quantity: parseFloat(r.quantity), notes: r.notes || null }));
    if (!lines.length) { setError('Add at least one product with a quantity.'); return; }
    setSaving(true);
    setError('');
    try {
      await axios.post(`${API_URL}/stock/inbound`, { warehouse_location_id: location.id, lines }, auth);
      onDone();
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to add stock.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="loc-overlay" onClick={onClose}>
      <div className="loc-modal loc-modal--wide" onClick={e => e.stopPropagation()}>
        <div className="loc-modal-header">
          <h3 className="loc-modal-title">Add Stock — {location.name}</h3>
          <button className="loc-modal-close" onClick={onClose}>✕</button>
        </div>

        <div className="loc-modal-body loc-modal-body--table">
          {error && <div className="loc-modal-error">{error}</div>}
          <table className="loc-stock-table">
            <thead>
              <tr>
                <th>Product</th>
                <th>Quantity</th>
                <th>Notes</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((row, i) => (
                <tr key={i}>
                  <td>
                    <ProductPicker
                      products={products}
                      value={row.product_id}
                      onChange={v => setRow(i, 'product_id', v)}
                    />
                  </td>
                  <td>
                    <input
                      className="loc-input loc-input--sm"
                      type="number"
                      min="0"
                      step="1"
                      placeholder="0"
                      value={row.quantity}
                      onChange={e => setRow(i, 'quantity', e.target.value)}
                    />
                  </td>
                  <td>
                    <input
                      className="loc-input loc-input--sm"
                      type="text"
                      placeholder="Optional"
                      value={row.notes}
                      onChange={e => setRow(i, 'notes', e.target.value)}
                    />
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>

        <div className="loc-modal-footer">
          <button className="loc-btn loc-btn--cancel" onClick={onClose}>Cancel</button>
          <button className="loc-btn loc-btn--save" onClick={handleSubmit} disabled={saving}>
            {saving ? 'Saving…' : 'Add Stock'}
          </button>
        </div>
      </div>
    </div>
  );
};

/* ── Stock Balance Modal ───────────────────────────────────── */
const StockBalanceModal = ({ location, auth, onClose, onUpdated }) => {
  const [items,        setItems]        = useState([]);
  const [loading,      setLoading]      = useState(true);
  const [error,        setError]        = useState('');
  const [removingItem, setRemovingItem] = useState(null);
  const [reason,       setReason]       = useState('');
  const [removeError,  setRemoveError]  = useState('');
  const [submitting,   setSubmitting]   = useState(false);

  const loadBalance = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const res = await axios.get(`${API_URL}/stock/balance`, { params: { location_id: location.id }, ...auth });
      setItems(res.data || []);
    } catch {
      setError('Failed to load stock balance.');
    } finally {
      setLoading(false);
    }
  }, [location.id]); // eslint-disable-line

  useEffect(() => { loadBalance(); }, [loadBalance]);

  const startRemove = (item) => { setRemovingItem(item); setReason(''); setRemoveError(''); };
  const cancelRemove = () => { setRemovingItem(null); setReason(''); setRemoveError(''); };

  const submitRemove = async () => {
    if (!reason.trim()) { setRemoveError('Removal reason is required.'); return; }
    setSubmitting(true);
    setRemoveError('');
    try {
      await axios.post(`${API_URL}/stock/adjust`, {
        location_id: location.id,
        product_id:  removingItem.product_id,
        quantity:    -parseFloat(removingItem.qty_on_hand),
        notes:       reason.trim(),
      }, auth);
      setRemovingItem(null);
      setReason('');
      await loadBalance();
      onUpdated();
    } catch (err) {
      setRemoveError(err.response?.data?.error || 'Failed to remove stock.');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="loc-overlay" onClick={onClose}>
      <div className="loc-modal loc-modal--wide" onClick={e => e.stopPropagation()}>
        <div className="loc-modal-header">
          <h3 className="loc-modal-title">Stock at {location.name}</h3>
          <button className="loc-modal-close" onClick={onClose}>✕</button>
        </div>

        <div className="loc-modal-body loc-modal-body--table">
          {error && <div className="loc-modal-error">{error}</div>}
          {loading ? (
            <div className="loc-loading">Loading…</div>
          ) : items.length === 0 ? (
            <div className="loc-empty">No stock on hand.</div>
          ) : (
            <table className="loc-stock-table">
              <thead>
                <tr>
                  <th>Product</th>
                  <th style={{ textAlign: 'right' }}>Qty on Hand</th>
                  <th></th>
                </tr>
              </thead>
              <tbody>
                {items.map(item => (
                  removingItem?.product_id === item.product_id ? (
                    <tr key={item.product_id} className="loc-removing-row">
                      <td colSpan={3}>
                        <div className="loc-reason-form">
                          <span className="loc-reason-product">{item.product_name}</span>
                          <input
                            className="loc-input loc-input--sm loc-reason-input"
                            type="text"
                            placeholder="Removal reason (required)"
                            value={reason}
                            autoFocus
                            onChange={e => setReason(e.target.value)}
                            onKeyDown={e => e.key === 'Enter' && submitRemove()}
                          />
                          {removeError && <span className="loc-reason-error">{removeError}</span>}
                          <div className="loc-reason-actions">
                            <button className="loc-btn loc-btn--cancel" onClick={cancelRemove}>Cancel</button>
                            <button className="loc-btn loc-btn--save loc-btn--danger-save" onClick={submitRemove} disabled={submitting}>
                              {submitting ? 'Removing…' : 'Submit'}
                            </button>
                          </div>
                        </div>
                      </td>
                    </tr>
                  ) : (
                    <tr key={item.product_id}>
                      <td>{item.product_name}</td>
                      <td style={{ textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>
                        {parseFloat(item.qty_on_hand).toLocaleString('en-IN', { maximumFractionDigits: 3 })}
                        {item.unit ? ` ${item.unit}` : ''}
                      </td>
                      <td style={{ textAlign: 'center' }}>
                        <button className="loc-icon-btn" title="Remove" onClick={() => startRemove(item)}>🗑️</button>
                      </td>
                    </tr>
                  )
                ))}
              </tbody>
            </table>
          )}
        </div>

        <div className="loc-modal-footer">
          <button className="loc-btn loc-btn--cancel" onClick={onClose}>Close</button>
        </div>
      </div>
    </div>
  );
};

/* ── Transfer Stock Modal ──────────────────────────────────── */
const TransferStockModal = ({ location, allLocations, auth, onClose, onDone }) => {
  const [toLocationId, setToLocationId] = useState('');
  const [items,        setItems]        = useState([]);    // balance rows with qty input
  const [loading,      setLoading]      = useState(true);
  const [saving,       setSaving]       = useState(false);
  const [error,        setError]        = useState('');

  const otherLocations = allLocations.filter(l => l.id !== location.id && l.is_active);

  useEffect(() => {
    axios.get(`${API_URL}/stock/balance`, { params: { location_id: location.id }, ...auth })
      .then(res => setItems((res.data || []).map(b => ({ ...b, transferQty: '' }))))
      .catch(() => setError('Failed to load stock.'))
      .finally(() => setLoading(false));
  }, []); // eslint-disable-line

  const setQty = (productId, value) =>
    setItems(prev => prev.map(b => b.product_id === productId ? { ...b, transferQty: value } : b));

  const handleSubmit = async () => {
    if (!toLocationId) { setError('Please select a destination location.'); return; }
    const lines = items
      .filter(b => parseFloat(b.transferQty) > 0)
      .map(b => ({ product_id: b.product_id, quantity: parseFloat(b.transferQty) }));
    if (!lines.length) { setError('Enter a quantity for at least one product.'); return; }
    const over = items.find(b => parseFloat(b.transferQty) > parseFloat(b.qty_on_hand));
    if (over) { setError(`Quantity for "${over.product_name}" exceeds available stock (${over.qty_on_hand}).`); return; }
    setSaving(true);
    setError('');
    try {
      await axios.post(`${API_URL}/stock/transfers`, {
        from_location_id: location.id,
        to_location_id:   Number(toLocationId),
        lines,
      }, auth);
      onDone();
    } catch (err) {
      setError(err.response?.data?.error || 'Transfer failed.');
    } finally {
      setSaving(false);
    }
  };

  return (
    <div className="loc-overlay" onClick={onClose}>
      <div className="loc-modal loc-modal--wide" onClick={e => e.stopPropagation()}>
        <div className="loc-modal-header">
          <h3 className="loc-modal-title">Transfer Stock from {location.name}</h3>
          <button className="loc-modal-close" onClick={onClose}>✕</button>
        </div>

        <div className="loc-modal-body loc-modal-body--table">
          {error && <div className="loc-modal-error">{error}</div>}

          {/* To Location */}
          <div className="loc-field loc-field--inline">
            <label className="loc-label">To Location</label>
            <select
              className="loc-form-select"
              value={toLocationId}
              onChange={e => setToLocationId(e.target.value)}
            >
              <option value="">— select destination —</option>
              {otherLocations.map(l => (
                <option key={l.id} value={l.id}>
                  {l.name} ({TYPE_LABEL[l.location_type] || l.location_type})
                </option>
              ))}
            </select>
          </div>

          {/* Products */}
          {loading ? (
            <div className="loc-loading">Loading stock…</div>
          ) : items.length === 0 ? (
            <div className="loc-empty">No stock on hand to transfer.</div>
          ) : (
            <table className="loc-stock-table loc-stock-table--transfer">
              <thead>
                <tr>
                  <th>Product</th>
                  <th style={{ textAlign: 'right' }}>On Hand</th>
                  <th style={{ textAlign: 'right' }}>Transfer Qty</th>
                </tr>
              </thead>
              <tbody>
                {items.map(b => (
                  <tr key={b.product_id}>
                    <td>{b.product_name}</td>
                    <td style={{ textAlign: 'right', color: '#888', fontVariantNumeric: 'tabular-nums' }}>
                      {parseFloat(b.qty_on_hand).toLocaleString('en-IN', { maximumFractionDigits: 3 })}
                      {b.unit ? ` ${b.unit}` : ''}
                    </td>
                    <td>
                      <input
                        className="loc-input loc-input--sm loc-input--right"
                        type="number"
                        min="0"
                        step="1"
                        max={b.qty_on_hand}
                        placeholder="0"
                        value={b.transferQty}
                        onChange={e => setQty(b.product_id, e.target.value)}
                      />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>

        <div className="loc-modal-footer">
          <button className="loc-btn loc-btn--cancel" onClick={onClose}>Cancel</button>
          <button className="loc-btn loc-btn--save" onClick={handleSubmit} disabled={saving || loading}>
            {saving ? 'Transferring…' : 'Submit'}
          </button>
        </div>
      </div>
    </div>
  );
};

/* ── Main Page ─────────────────────────────────────────────── */
const Locations = () => {
  const { user } = useContext(AuthContext);
  const auth = { headers: { Authorization: `Bearer ${user?.token}` } };

  const [locations,      setLocations]      = useState([]);
  const [loading,        setLoading]        = useState(true);
  const [error,          setError]          = useState('');

  const [search,         setSearch]         = useState('');
  const [typeFilter,     setTypeFilter]     = useState('');
  const [showActive,     setShowActive]     = useState('');

  const [modalTarget,     setModalTarget]     = useState(null);
  const [addStockTarget,  setAddStockTarget]  = useState(null);
  const [stockViewTarget, setStockViewTarget] = useState(null);
  const [transferTarget,  setTransferTarget]  = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    setError('');
    try {
      const params = {};
      if (typeFilter)       params.type   = typeFilter;
      if (showActive !== '') params.active = showActive;
      if (search.trim())    params.search = search.trim();
      const res = await axios.get(`${API_URL}/stock/locations`, { params, ...auth });
      setLocations(res.data || []);
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to load locations.');
    } finally {
      setLoading(false);
    }
  }, [typeFilter, showActive, search]); // eslint-disable-line

  useEffect(() => { load(); }, [load]);

  const handleSaved = () => { setModalTarget(null); load(); };
  const handleStockDone = () => { setAddStockTarget(null); load(); };

  return (
    <div className="loc-page">
      {/* Header */}
      <div className="loc-header">
        <div>
          <h1 className="loc-title">Stock Locations</h1>
          <p className="loc-subtitle">Warehouses, delivery boys, stores, and vending machines</p>
        </div>
        <button className="loc-btn loc-btn--add" onClick={() => setModalTarget(false)}>
          + Add Location
        </button>
      </div>

      {error && <div className="loc-error">{error}</div>}

      {/* Filters */}
      <div className="loc-filter-row">
        <input
          className="loc-search-input"
          type="text"
          placeholder="Search name or address…"
          value={search}
          onChange={e => setSearch(e.target.value)}
        />
        <select className="loc-select" value={typeFilter} onChange={e => setTypeFilter(e.target.value)}>
          <option value="">All types</option>
          {Object.entries(TYPE_LABEL).map(([v, l]) => (
            <option key={v} value={v}>{l}</option>
          ))}
        </select>
        <select className="loc-select" value={showActive} onChange={e => setShowActive(e.target.value)}>
          <option value="">All statuses</option>
          <option value="true">Active</option>
          <option value="false">Inactive</option>
        </select>
        <span className="loc-count">{locations.length} location{locations.length !== 1 ? 's' : ''}</span>
      </div>

      {/* Table */}
      {loading ? (
        <div className="loc-loading">Loading…</div>
      ) : locations.length === 0 ? (
        <div className="loc-empty">No locations found.</div>
      ) : (
        <div className="loc-table-wrap">
          <table className="loc-table">
            <thead>
              <tr>
                <th>Name</th>
                <th>Type</th>
                <th>Reference / User</th>
                <th>Address</th>
                <th className="loc-col-num">Products</th>
                <th>Status</th>
                <th>Actions</th>
              </tr>
            </thead>
            <tbody>
              {locations.map(loc => (
                <tr key={loc.id} className={!loc.is_active ? 'loc-row--inactive' : ''}>
                  <td className="loc-name">{loc.name}</td>
                  <td>
                    <span className={`loc-badge ${TYPE_CLASS[loc.location_type] || ''}`}>
                      {TYPE_LABEL[loc.location_type] || loc.location_type}
                    </span>
                  </td>
                  <td>{loc.reference_name || '—'}</td>
                  <td>
                    {loc.address
                      ? <span className="loc-addr" title={loc.address}>{loc.address}</span>
                      : <span style={{ color: '#ccc' }}>—</span>}
                  </td>
                  <td className="loc-col-num">
                    {(loc.product_count ?? 0) > 0
                      ? <button className="loc-count-link" onClick={() => setStockViewTarget(loc)}>{loc.product_count}</button>
                      : 0}
                  </td>
                  <td>
                    <span className={`loc-status ${loc.is_active ? 'loc-status--active' : 'loc-status--inactive'}`}>
                      {loc.is_active ? 'Active' : 'Inactive'}
                    </span>
                  </td>
                  <td>
                    <div className="loc-action-cell">
                      <button
                        className="loc-icon-btn"
                        title="Edit"
                        onClick={() => setModalTarget(loc)}
                      >✏️</button>
                      <button
                        className="loc-icon-btn"
                        title="Add Stock"
                        onClick={() => setAddStockTarget(loc)}
                      >📦</button>
                      <button
                        className="loc-icon-btn"
                        title="Transfer Stock"
                        onClick={() => setTransferTarget(loc)}
                      >↔️</button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Edit / Add Location Modal */}
      {modalTarget !== null && (
        <LocationModal
          initial={modalTarget || null}
          auth={auth}
          onClose={() => setModalTarget(null)}
          onSaved={handleSaved}
        />
      )}

      {/* Add Stock Modal */}
      {addStockTarget && (
        <AddStockModal
          location={addStockTarget}
          auth={auth}
          onClose={() => setAddStockTarget(null)}
          onDone={handleStockDone}
        />
      )}

      {/* Stock Balance / Remove Modal */}
      {stockViewTarget && (
        <StockBalanceModal
          location={stockViewTarget}
          auth={auth}
          onClose={() => setStockViewTarget(null)}
          onUpdated={load}
        />
      )}

      {/* Transfer Stock Modal */}
      {transferTarget && (
        <TransferStockModal
          location={transferTarget}
          allLocations={locations}
          auth={auth}
          onClose={() => setTransferTarget(null)}
          onDone={() => { setTransferTarget(null); load(); }}
        />
      )}
    </div>
  );
};

export default Locations;
